import React from 'react';
import { DealDocument } from '_business/types/TransactionsDocuments';
import { Button, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, PaperClipOutlined } from '@ant-design/icons';
import { CategoryDocument } from '_business/const/DocumentsCategories';
import { formatDate } from '_technical/utils/date';
import { useDownloadDocument } from 'views/projects/products_hooks';
import { useResponsive } from 'antd-style';
import clsx from 'clsx';
import { compareAsc } from 'date-fns';
import { extractDocumentName } from '_technical/views/projects/Details/DealDocuments/document-format';

const { Column } = Table;

type DocumentItem = DealDocument & { category: CategoryDocument };

interface DealDocumentsTableProps {
  loading: boolean;
  files: DocumentItem[];
  onUpdate: (document: DocumentItem) => void;
}

export default function DealDocumentsTable({
  loading,
  files,
  onUpdate,
}: DealDocumentsTableProps) {
  const { t } = useTranslation('documents');
  const { mobile } = useResponsive();

  const sortFilesByDateAdded = (files: DocumentItem[]) => {
    const f = files;
    f.sort((a: DocumentItem, b: DocumentItem) =>
      compareAsc(b.createdAt, a.createdAt),
    );

    return f;
  };

  return mobile ? (
    <div className={'flex flex-col gap-6'}>
      {files.map((record) => (
        <div key={record.id} className={'flex gap-2 items-center'}>
          {<Icon item={record} />}
          <div className={'grow flex flex-col gap-2'}>
            <Name className={'grow'} item={record} />
            <Category item={record} />
            <SendBy item={record} />
          </div>
          <Actions item={record} />
        </div>
      ))}
      {files.length === 0 && (
        <Typography.Paragraph className={'text-xs text-GrisHellio-5'}>
          {t('empty')}
        </Typography.Paragraph>
      )}
    </div>
  ) : (
    <Table<DocumentItem>
      loading={loading}
      pagination={false}
      dataSource={sortFilesByDateAdded(files)}
      className={
        'max-w-full ant-table-header-border ant-table-sorter-start ant-table-header-gray'
      }
      // sticky={{ offsetHeader: 16 }}
      rowKey={'id'}
      showSorterTooltip={{ target: 'sorter-icon' }}
      tableLayout={'auto'}
      tailor
    >
      <Column
        showSorterTooltip={{ target: 'full-header' }}
        title={`${t('file')}`}
        dataIndex={'name'}
        key={'name'}
        sorter={(a: DocumentItem, b: DocumentItem) => {
          return extractDocumentName(a.name).localeCompare(
            extractDocumentName(b.name),
          );
        }}
        render={(_, record: DocumentItem) => {
          return (
            <div className={'flex items-center gap-2'}>
              {<Icon item={record} />}
              <div className={'flex flex-col gap-2'}>
                <Name item={record} />
                <SendBy item={record} />
              </div>
            </div>
          );
        }}
      />
      <Column
        title={`${t('file_type')}`}
        dataIndex={'category'}
        render={(v) => t(`documents_category:${v}`)}
        key={'category'}
        className={'text-GrisHellio-5 text-xs'}
        sorter={(a: DocumentItem, b: DocumentItem) =>
          t(`documents_category:${a.category}`).localeCompare(
            t(`documents_category:${b.category}`),
          )
        }
      />

      <Column
        minWidth={80}
        align={'center'}
        title={`${t('actions')}`}
        dataIndex={'actions'}
        key={'actions'}
        render={(v, item: DocumentItem) => {
          return <Actions className={'w-full -mr-2'} item={item} />;
        }}
      />
    </Table>
  );
}

export const getFileIcon = (extension: string) => {
  return <PaperClipOutlined />;
};

export const SendBy = ({ item }: { item: DealDocument }) => {
  const { t } = useTranslation('documents');
  return (
    <Typography.Paragraph className={'!leading-none text-xs text-GrisHellio-5'}>
      {t(
        item.name.includes('CUSTOMER') || item.name.includes('_Client')
          ? 'by_me'
          : 'by_hellio',
        {
          date: formatDate(item.createdAt, 'dd MMM yyyy'),
        },
      )}
    </Typography.Paragraph>
  );
};

const Name = ({
  item,
  className,
}: {
  item: DocumentItem;
  className?: string;
}) => {
  return (
    <Typography.Paragraph className={clsx('text-xs font-bold', className)}>
      {extractDocumentName(item.name)}.{item.extension}
    </Typography.Paragraph>
  );
};
const Category = ({ item }: { item: DocumentItem }) => {
  const { t } = useTranslation('documents');
  return (
    <Typography.Paragraph className={'!leading-none text-xs text-GrisHellio-5'}>
      {t(`documents_category:${item.category}`)}
    </Typography.Paragraph>
  );
};

const Icon = ({ item }: { item: DocumentItem }) => {
  return (
    <div
      className={
        'h-8 min-w-8 rounded-md bg-BleuHellio-1 text-primary flex items-center justify-center'
      }
    >
      {getFileIcon(item.extension)}
    </div>
  );
};

export const Actions = ({
  item,
  className,
}: {
  item: DealDocument;
  className?: string;
}) => {
  const { t } = useTranslation('documents');
  const { download, loading } = useDownloadDocument();
  return (
    <div className={clsx('flex justify-center', className)}>
      <Button
        onClick={() => download(item.id)}
        loading={loading}
        title={t('download')}
        className={'text-primary'}
        icon={<DownloadOutlined />}
      />
    </div>
  );
};
